import {
  Box,
  VStack,
  Text,
  HStack,
  Badge,
  Icon,
  Separator,
} from "@chakra-ui/react";
import ResultEntryTable from "./ResultEntryTable";
import ResultTableInfo from "./ResultTableInfo";
import {
  BehavioralResultEntry,
  BehavioralSubject,
  CognitiveResultEntry,
  Student,
  Subject,
  SummaryResultEntry,
} from "../../../services/Dtos";
import { useStyles } from "../../../hooks/useStyles";
import { Dispatch, SetStateAction } from "react";
import { BiInfoCircle, BiArrowBack } from "react-icons/bi";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import useThemeColors from "../../../hooks/useThemeColors";
import Cad from "../../CustomUi/Cad";
import CustomButton from "../../CustomUi/CustomButton";
import { useIcons } from "../../../hooks/useIcons";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import { Role } from "../../../services/Enums";
import { useBreakPoint } from "../../../hooks/useBreakPoint";


interface ResultsEntryPanelProps {
  chosenStudent: Student | null;
  selectedClassId: string;
  currentClassSubjects: Subject[];
  currentBehavioralSubjects: BehavioralSubject[];
  editingSubjectIds: string[];
  setEditingSubjectIds: Dispatch<SetStateAction<string[]>>;
  fetchingClassResult: boolean;
  cognitiveResultEntries: CognitiveResultEntry[];
  behavioralResultEntries: BehavioralResultEntry[];
  editedCognitiveEntries: CognitiveResultEntry[];
  editedBehavioralEntries: BehavioralResultEntry[];
  setEditedCognitiveEntries: Dispatch<SetStateAction<CognitiveResultEntry[]>>;
  setEditedBehavioralEntries: Dispatch<SetStateAction<BehavioralResultEntry[]>>;
  summaryResultEntry: SummaryResultEntry;
  editedSummaryEntry: SummaryResultEntry;
  setEditedSummaryEntry: Dispatch<SetStateAction<SummaryResultEntry>>;
  setSummaryResultEntry: Dispatch<SetStateAction<SummaryResultEntry>>;
  filteredAssessmentTypes: any[];
  filteredGradeCriterias: any[];
  currentLevel: string;
  setUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  unsavedChanges: boolean;
  currentSessionName?: string;
  currentTermName?: string;
  selectedClassName?: string;
  handleSave: () => void;
  savingStudentResultEntries: boolean;
  handlePreview: () => void;
  validResult: boolean;
  hasSummaryResultEntries: boolean;
  onBackToStudents?: () => void;
}

function ResultsEntryPanel({
  chosenStudent,
  selectedClassId,
  currentClassSubjects,
  currentBehavioralSubjects,
  editingSubjectIds,
  setEditingSubjectIds,
  fetchingClassResult,
  cognitiveResultEntries,
  behavioralResultEntries,
  editedCognitiveEntries,
  editedBehavioralEntries,
  setEditedCognitiveEntries,
  setEditedBehavioralEntries,
  summaryResultEntry,
  editedSummaryEntry,
  setEditedSummaryEntry,
  setSummaryResultEntry,
  filteredAssessmentTypes,
  filteredGradeCriterias,
  currentLevel,
  setUnsavedChanges,
  unsavedChanges,
  currentSessionName,
  currentTermName,
  selectedClassName,
  handleSave,
  savingStudentResultEntries,
  handlePreview,
  validResult,
  hasSummaryResultEntries,
  onBackToStudents,
}: ResultsEntryPanelProps) {
  const { scrollBar } = useStyles();
  const { school } = useSchoolsContext();
  const { hasRoleOrHigher } = useRoleAccess();
  const colors = useThemeColors();
  const { isMobile, isTablet } = useBreakPoint();
  const icons = useIcons();
  // Check if current term is a leap term
  const isLeapTerm =
    school?.currentTermId && school?.currentTermId === school?.leapTermId;

  return (
    <Cad h="100%" px={"0px"} py={"0px"} overflow="hidden">
      <VStack gap={0} align="stretch" h="100%">
        {/* Results Header */}
        <Box
          p={5}
          pb={0}
          borderBottom="1px solid"
          borderBottomColor={colors.border.primary}
          flexShrink={0}
        >
          <VStack gap={3} align="stretch">
            <HStack
              justify="space-between"
              align={isMobile ? "flex-start" : "center"}
              flexDir={isMobile ? "column" : "row"}
            >
              <VStack gap={1} align="start" flex={1}>
                <HStack gap={2} align="center" w="full">
                  <Icon
                    as={icons.main.result}
                    boxSize={5}
                    color={colors.schoolAccent.icon}
                  />
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color={colors.text.primary}
                  >
                    Results Entry
                  </Text>
                  {isLeapTerm && (
                    <Box
                      title="This is a Leap Term (Cumulative Term). Results entered here will also be used for cumulative result calculations."
                      cursor="help"
                    >
                      <Badge
                        colorPalette={colors.schoolAccent.primary}
                        px={2}
                        py={1}
                        fontSize="xs"
                        _hover={{
                          transform: "scale(1.05)",
                          transition: "transform 0.2s",
                        }}
                        variant="solid"
                      >
                        <Icon as={BiInfoCircle} mr={1} />
                        Leap Term
                      </Badge>
                    </Box>
                  )}
                </HStack>
                <HStack gap={2} align="center" w="full">
                  {isTablet && onBackToStudents && (
                    <CustomButton
                      size="sm"
                      variant="ghost"
                      icon={BiArrowBack}
                      label="Back"
                      onClick={onBackToStudents}
                      colorPalette={colors.schoolAccent.primary}
                    />
                  )}

                  <Text fontSize="sm" color={colors.text.secondary}>
                    {chosenStudent
                      ? `Editing results for ${chosenStudent.firstName} ${chosenStudent.lastName}`
                      : "Select a student to begin entering results"}
                  </Text>
                </HStack>
              </VStack>
              <HStack gap={2}>
                {unsavedChanges && (
                  <Badge colorPalette="orange" px={3} py={1} fontSize="xs">
                    Unsaved Changes
                  </Badge>
                )}
              </HStack>
            </HStack>

            <Separator />

            {/* Session/Term Info - Hidden on mobile, buttons moved to bottom */}
            <Box display={{ base: "none", md: "block" }}>
              <ResultTableInfo
                currentSessionName={currentSessionName}
                currentTermName={currentTermName}
                selectedClassName={selectedClassName}
                selectedStudentName={
                  chosenStudent
                    ? `${chosenStudent?.firstName} ${chosenStudent?.lastName}`
                    : undefined
                }
                handleSave={handleSave}
                unsavedChanges={unsavedChanges}
                savingStudentResultEntries={savingStudentResultEntries}
                handlePreview={handlePreview}
                validResult={validResult}
                hasSummaryResultEntries={hasSummaryResultEntries}
              />
            </Box>
          </VStack>
        </Box>

        {/* Results Content  */}
        <Box flex={1} overflowY="auto" p={4} minH={0} css={scrollBar}>
      
          <ResultEntryTable
            currentLevel={currentLevel}
            setUnsavedChanges={setUnsavedChanges}
            chosenStudent={chosenStudent}
            selectedClassId={selectedClassId}
            currentClassSubjects={currentClassSubjects}
            editingSubjectIds={editingSubjectIds}
            setEditingSubjectIds={setEditingSubjectIds}
            fetchingClassResult={fetchingClassResult}
            cognitiveResultEntries={cognitiveResultEntries}
            behavioralResultEntries={behavioralResultEntries}
            editedCognitiveEntries={editedCognitiveEntries}
            editedBehavioralEntries={editedBehavioralEntries}
            setEditedCognitiveEntries={setEditedCognitiveEntries}
            setEditedBehavioralEntries={setEditedBehavioralEntries}
            currentBehavioralSubjects={currentBehavioralSubjects}
            summaryResultEntry={summaryResultEntry}
            editedSummaryEntry={editedSummaryEntry}
            setEditedSummaryEntry={setEditedSummaryEntry}
            setSummaryResultEntry={setSummaryResultEntry}
            filteredAssessmentTypes={filteredAssessmentTypes}
            filteredGradeCriterias={filteredGradeCriterias}
          />
        </Box>

        {/* Mobile Action Buttons - Fixed at bottom */}
        {isMobile && chosenStudent && hasRoleOrHigher(Role.Editor) && (
          <Box
            p={4}
            borderTop="1px solid"
            borderTopColor={colors.border.primary}
            bg={colors.background.primary}
            flexShrink={0}
          >
            <VStack gap={3}>
              <HStack gap={3} w="full">
                <CustomButton
                  flex={1}
                  colorPalette="green"
                  label="Save Results"
                  onClick={handleSave}
                  loading={savingStudentResultEntries}
                  disabled={!unsavedChanges}
                  size="lg"
                />
                <CustomButton
                  flex={1}
                  variant="outline"
                  label="Preview"
                  onClick={handlePreview}
                  disabled={!validResult || !hasSummaryResultEntries}
                  size="lg"
                />
              </HStack>
              {unsavedChanges && (
                <Text fontSize="xs" color="orange.500" textAlign="center">
                  You have unsaved changes
                </Text>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Cad>
  );
}

export default ResultsEntryPanel;
